// services/orcamentoService.js
import { supabase } from '../lib/supabase';

/**
 * Busca um drink com seus ingredientes
 * @param {string} drinkId - ID do drink
 * @returns {Promise<Object>} - Drink com ingredientes
 */
export const getDrinkComIngredientes = async (drinkId) => {
  try {
    // Buscar o drink
    const { data: drink, error: drinkError } = await supabase
      .from('drinks')
      .select('*')
      .eq('id', drinkId)
      .single();

    if (drinkError) throw drinkError;
    if (!drink) throw new Error('Drink não encontrado');

    // Buscar os ingredientes do drink
    const { data: drinkIngredients, error: ingredientsError } = await supabase
      .from('drink_ingredients')
      .select(`
        quantity,
        e_decorativo,
        ingredients (id, name, unit, preco_standard, preco_premium)
      `)
      .eq('drink_id', drinkId);

    if (ingredientsError) throw ingredientsError;

    // Formatar os ingredientes
    const ingredientes = drinkIngredients.map(di => ({
      ingrediente_id: di.ingredients.id,
      nome: di.ingredients.name,
      quantidade: parseFloat(di.quantity),
      unidade: di.ingredients.unit,
      preco_standard: di.ingredients.preco_standard,
      preco_premium: di.ingredients.preco_premium,
      e_decorativo: di.e_decorativo
    }));

    return {
      ...drink,
      ingredientes
    };
  } catch (error) {
    console.error('Erro ao buscar drink com ingredientes:', error);
    throw error;
  }
};

/**
 * Busca uma marca específica para um ingrediente baseado no perfil
 * @param {string} ingredienteId - ID do ingrediente
 * @param {string} perfil - Perfil de preço (standard ou premium)
 * @returns {Promise<Object|null>} - Marca encontrada ou null
 */
export const getMarcaPorPerfil = async (ingredienteId, perfil) => {
  try {
    // Na implementação atual, usamos diretamente os preços do ingrediente
    // Em uma implementação futura, poderíamos buscar marcas específicas
    const { data, error } = await supabase
      .from('ingredients')
      .select('id, name, preco_standard, preco_premium')
      .eq('id', ingredienteId)
      .single();

    if (error) throw error;
    if (!data) return null;

    // Simular uma "marca" com o preço adequado
    return {
      id: data.id,
      nome: data.name,
      perfil,
      preco: perfil === 'premium' ? data.preco_premium : data.preco_standard
    };
  } catch (error) {
    console.error('Erro ao buscar marca por perfil:', error);
    return null;
  }
};

/**
 * Calcula o orçamento para um drink
 * @param {string} drinkId - ID do drink
 * @param {string} perfil - Perfil de preço (standard ou premium)
 * @param {number} quantidade - Quantidade do drink
 * @returns {Promise<Object>} - Orçamento calculado
 */
export const calcularOrcamento = async (drinkId, perfil = 'standard', quantidade = 1) => {
  try {
    const drink = await getDrinkComIngredientes(drinkId);
    
    let custoTotal = 0;
    const detalhes = [];
    
    for (const item of drink.ingredientes) {
      // Pular ingredientes decorativos no cálculo de custo
      if (item.e_decorativo) continue;
      
      // Buscar marca específica do perfil
      const marca = await getMarcaPorPerfil(item.ingrediente_id, perfil);
      const preco = marca ? marca.preco : (perfil === 'premium' ? item.preco_premium : item.preco_standard);
      
      const custo = (item.quantidade * preco * quantidade);
      custoTotal += custo;
      
      detalhes.push({
        ingrediente: item.nome,
        marca: marca?.nome || 'Genérico',
        quantidade: item.quantidade,
        unidade: item.unidade,
        preco_unitario: preco,
        custo: custo
      });
    }
    
    return { 
      drink: {
        id: drink.id,
        nome: drink.name,
        categoria: drink.category,
        descricao: drink.description
      },
      custoTotal, 
      detalhes, 
      perfil 
    };
  } catch (error) {
    console.error('Erro ao calcular orçamento:', error);
    throw error;
  }
};

/**
 * Compara orçamentos standard e premium para um drink
 * @param {string} drinkId - ID do drink
 * @param {number} quantidade - Quantidade do drink
 * @returns {Promise<Object>} - Comparação dos orçamentos
 */
export const compararOrcamentos = async (drinkId, quantidade = 1) => {
  try {
    const [standard, premium] = await Promise.all([
      calcularOrcamento(drinkId, 'standard', quantidade),
      calcularOrcamento(drinkId, 'premium', quantidade)
    ]);
    
    const diferenca = premium.custoTotal - standard.custoTotal;
    const percentual = standard.custoTotal > 0 
      ? (diferenca / standard.custoTotal * 100)
      : 0;
    
    return {
      standard,
      premium,
      diferenca,
      percentual: percentual.toFixed(2)
    };
  } catch (error) {
    console.error('Erro ao comparar orçamentos:', error);
    throw error;
  }
};

/**
 * Calcula orçamento para múltiplos drinks
 * @param {Array<{id: string, quantidade: number}>} drinks - Lista de drinks e quantidades
 * @param {string} perfil - Perfil de preço (standard, premium ou both)
 * @returns {Promise<Object>} - Orçamento calculado
 */
export const calcularOrcamentoMultiplo = async (drinks, perfil = 'standard') => {
  try {
    if (perfil === 'both') {
      // Calcular ambos os perfis
      const [standard, premium] = await Promise.all([
        Promise.all(drinks.map(d => calcularOrcamento(d.id, 'standard', d.quantidade))),
        Promise.all(drinks.map(d => calcularOrcamento(d.id, 'premium', d.quantidade)))
      ]);
      
      const custoTotalStandard = standard.reduce((sum, item) => sum + item.custoTotal, 0);
      const custoTotalPremium = premium.reduce((sum, item) => sum + item.custoTotal, 0);
      
      return {
        standard: {
          drinks: standard,
          custoTotal: custoTotalStandard
        },
        premium: {
          drinks: premium,
          custoTotal: custoTotalPremium
        },
        diferenca: custoTotalPremium - custoTotalStandard,
        percentual: custoTotalStandard > 0 
          ? ((custoTotalPremium - custoTotalStandard) / custoTotalStandard * 100).toFixed(2)
          : 0
      };
    } else {
      // Calcular apenas um perfil
      const resultados = await Promise.all(
        drinks.map(d => calcularOrcamento(d.id, perfil, d.quantidade))
      );
      
      const custoTotal = resultados.reduce((sum, item) => sum + item.custoTotal, 0);
      
      return {
        drinks: resultados,
        custoTotal,
        perfil
      };
    }
  } catch (error) {
    console.error('Erro ao calcular orçamento múltiplo:', error);
    throw error;
  }
};

/**
 * Formata um valor monetário
 * @param {number} valor - Valor a ser formatado
 * @returns {string} - Valor formatado como moeda
 */
export const formatarMoeda = (valor) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(valor);
};